
import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Modal, Input, Button, message } from 'antd';
import {
    EyeOutlined,
    FileTextOutlined,
    EditOutlined,
    CopyOutlined,
    PlusOutlined,
    DeleteOutlined,
    HolderOutlined,
    LeftOutlined,
    RightOutlined
} from '@ant-design/icons';
import toast from "react-hot-toast";

const CardBasedSteps = ({ watchedSteps, currentStep, onStepChange, onStepsUpdate }) => {
    const [selectedCards, setSelectedCards] = useState([]);
    const [steps, setSteps] = useState([]);
    const [editingStepName, setEditingStepName] = useState(null);
    const [stepNameValue, setStepNameValue] = useState('');
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const scrollContainerRef = useRef(null);
    const stickyRef = useRef(null);

    // Check if mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Initialize steps with Plus card and Review & Submit card
    useEffect(() => {
        const plusCard = {
            id: 'add-new-step',
            name: "Add Step",
            icon: PlusOutlined,
            type: 'add',
            isAddCard: true
        };

        const reviewCard = {
            id: 'review-submit',
            name: "Review & Submit",
            icon: EyeOutlined,
            type: 'review',
            isReviewCard: true
        };

        const stepsWithIds = (watchedSteps || []).map((step, index) => ({
            ...step,
            id: step.id || `step-${index}`,
            type: 'regular'
        }));

        setSteps([...stepsWithIds, plusCard, reviewCard]);
    }, [watchedSteps]);

    // Initialize selectedCards based on steps length
    useEffect(() => {
        setSelectedCards(new Array(steps.length).fill(false));
    }, [steps.length]);

    // Update selected cards when currentStep changes from parent
    useEffect(() => {
        if (currentStep !== undefined && currentStep >= 0) {
            setSelectedCards(prev => {
                const newSelected = new Array(steps.length).fill(false);
                newSelected[currentStep] = true;
                return newSelected;
            });
        }
    }, [currentStep, steps.length]);

    // Sticky behavior - positioned below the header
    useEffect(() => {
        let ticking = false;
        let initialOffsetTop = 0;

        if (stickyRef.current) {
            // On mobile, account for the fixed header height (128px)
            initialOffsetTop = isMobile ? 128 : stickyRef.current.offsetTop;
        }

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const windowHeight = window.innerHeight;
                    const documentHeight = document.documentElement.scrollHeight;
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

                    const isNearBottom = scrollTop + windowHeight >= documentHeight - 100;
                    const newSticky = scrollTop >= initialOffsetTop && !isNearBottom;

                    setIsSticky(prev => (prev !== newSticky ? newSticky : prev));
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isMobile]);

    // Check scroll buttons visibility
    const checkScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Handle scroll events
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            checkScrollButtons();

            const handleResize = () => {
                setTimeout(checkScrollButtons, 100);
            };
            window.addEventListener('resize', handleResize);

            return () => {
                container.removeEventListener('scroll', checkScrollButtons);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [steps]);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -200,
                behavior: 'smooth'
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: 200,
                behavior: 'smooth'
            });
        }
    };

    // Auto scroll to active card when currentStep changes
    useEffect(() => {
        if (currentStep >= 0 && scrollContainerRef.current) {
            const activeCard = scrollContainerRef.current.querySelector(`[data-step-index="${currentStep}"]`);
            if (activeCard) {
                activeCard.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [currentStep]);

    // Generate default step structure
    const generateDefaultStep = (name) => ({
        id: `step-${Date.now()}`,
        name: name || `New Step ${steps.length - 1}`,
        type: 'regular',
        components: [
            {
                name: "sample_image",
                type: "image",
                required: false,
                isCommon: false,
                placeholderimage: "",
                options: null
            },
            {
                name: "sample_text",
                type: "textarea",
                required: false,
                isCommon: false,
                placeholder: "",
                options: null
            }
        ],
        commonComponents: [],
        units: [],
        commonData: {}
    });

    const handleCardClick = (index) => {
        const step = steps[index];

        if (step.isAddCard) {
            const newStep = generateDefaultStep();
            const newSteps = [...steps];
            newSteps.splice(index, 0, newStep);
            setSteps(newSteps);

            if (onStepsUpdate) {
                onStepsUpdate(newSteps.filter(s => !s.isAddCard && !s.isReviewCard));
            }
            return;
        }

        setSelectedCards(prev => {
            const newSelected = new Array(steps.length).fill(false);
            newSelected[index] = true;
            return newSelected;
        });

        if (onStepChange) {
            onStepChange(index);
        }
    };

    const handleClone = (index, e) => {
        e.stopPropagation();
        const stepToClone = steps[index];

        if (stepToClone.isReviewCard || stepToClone.isAddCard) return;

        const clonedStep = {
            ...JSON.parse(JSON.stringify(stepToClone)),
            id: `${stepToClone.id}-clone-${Date.now()}`,
            name: `${stepToClone.name} (Copy)`
        };

        const newSteps = [...steps];
        newSteps.splice(index + 1, 0, clonedStep);
        setSteps(newSteps);

        if (onStepsUpdate) {
            onStepsUpdate(newSteps.filter(s => !s.isAddCard && !s.isReviewCard));
        }
    };

    const handleDeleteStep = (index, e) => {
        e.stopPropagation();
        const newSteps = steps.filter((_, i) => i !== index);
        setSteps(newSteps);

        if (onStepsUpdate) {
            onStepsUpdate(newSteps.filter(s => !s.isAddCard && !s.isReviewCard));
        }
    };

    const handleStepNameEdit = (index, e) => {
        e.stopPropagation();
        const step = steps[index];

        if (step.isAddCard || step.isReviewCard) return;

        setEditingStepName(index);
        setStepNameValue(step.name);
    };

    const handleStepNameSave = (index) => {
        if (stepNameValue.trim()) {
            const newSteps = [...steps];
            newSteps[index] = {
                ...newSteps[index],
                name: stepNameValue.trim()
            };
            setSteps(newSteps);

            if (onStepsUpdate) {
                onStepsUpdate(newSteps.filter(s => !s.isAddCard && !s.isReviewCard));
            }

            toast.success('Step name updated successfully');
        }
        setEditingStepName(null);
        setStepNameValue('');
    };

    const handleStepNameCancel = () => {
        setEditingStepName(null);
        setStepNameValue('');
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const draggedStep = steps[source.index];
        if (draggedStep.isAddCard || draggedStep.isReviewCard) return;

        const targetStep = steps[destination.index];
        if (targetStep?.isAddCard || targetStep?.isReviewCard) return;

        const newSteps = Array.from(steps);
        const [reorderedItem] = newSteps.splice(source.index, 1);
        newSteps.splice(destination.index, 0, reorderedItem);

        setSteps(newSteps);

        if (onStepsUpdate) {
            onStepsUpdate(newSteps.filter(s => !s.isAddCard && !s.isReviewCard));
        }

        if (selectedCards[source.index]) {
            setSelectedCards(prev => {
                const newSelected = new Array(newSteps.length).fill(false);
                newSelected[destination.index] = true;
                return newSelected;
            });
        }
    };

    const getCardStatus = (index) => {
        return selectedCards[index] ? 'active' : 'pending';
    };

    const getCardClasses = (status, step) => {
        const baseClasses = "relative overflow-hidden transition-all duration-200 rounded-xl cursor-pointer rounded-md border flex items-center";

        if (step?.isAddCard) {
            return `${baseClasses} bg-green-200 border-green-200 hover:bg-green-100 hover:border-green-300 h-8`;
        }

        if (step?.isReviewCard) {
            return `${baseClasses} bg-white border-gray-200 hover:bg-gray-100 hover:border-gray-300 h-8`;
        }

        switch (status) {
            case 'active':
                return `${baseClasses} bg-gradient-to-r from-[#0087af] to-[#00ae68] border-green-600 text-white shadow-md h-8`;
            default:
                return `${baseClasses} bg-white border-gray-200 hover:border-green-300 hover:shadow-sm h-8`;
        }
    };

    return (
        <>
            <div
                ref={stickyRef}
                className={`transition-all duration-300 ${isSticky && isMobile
                    ? `fixed left-0 right-0 bg-white shadow-md  rounded-xl z-[50] ${isMobile ? 'top-6' : 'top-0'
                    }`
                    : 'relative'
                    }`}
                style={{
                    ...(isSticky && isMobile && {
                        paddingTop: '8px',
                        paddingBottom: '8px'
                    })
                }}
            >
                <div className={`${isSticky && isMobile ? 'max-w-7xl mx-auto px-4' : ''}`}>
                    <div className={`${isSticky && isMobile ? 'bg-gray-50 rounded-lg px-3 py-1' : 'py-1'}`}>
                        {/* Single Line Scrollable Container */}
                        <div className="relative">
                            {/* Left Scroll Button */}
                            {canScrollLeft && (
                                <button
                                    onClick={scrollLeft}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-1 transition-all duration-200 hover:shadow-lg"
                                    style={{ marginLeft: '-4px' }}
                                >
                                    <LeftOutlined className="text-gray-600" style={{ fontSize: '10px' }} />
                                </button>
                            )}

                            {/* Right Scroll Button */}
                            {canScrollRight && (
                                <button
                                    onClick={scrollRight}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-1 transition-all duration-200 hover:shadow-lg"
                                    style={{ marginRight: '-4px' }}
                                >
                                    <RightOutlined className="text-gray-600" style={{ fontSize: '10px' }} />
                                </button>
                            )}

                            {/* Single Line Scrollable Content */}
                            <div
                                ref={scrollContainerRef}
                                className="overflow-x-auto scrollbar-hide"
                                style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    paddingLeft: canScrollLeft ? '16px' : '0',
                                    paddingRight: canScrollRight ? '16px' : '0'
                                }}
                            >
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="steps-list" direction="horizontal">
                                        {(provided, snapshot) => (
                                            <div
                                                className="flex gap-2 "
                                                style={{ minWidth: 'max-content' }}
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {steps.map((step, index) => {
                                                    const status = getCardStatus(index);
                                                    const canModify = !step.isReviewCard && !step.isAddCard;
                                                    const isDraggable = !step.isAddCard && !step.isReviewCard;

                                                    return (
                                                        <Draggable
                                                            key={step.id}
                                                            draggableId={step.id}
                                                            index={index}
                                                            isDragDisabled={!isDraggable}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    data-step-index={index}
                                                                    className="flex-shrink-0"
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        transform: snapshot.isDragging
                                                                            ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                                                                            : provided.draggableProps.style?.transform
                                                                    }}
                                                                >
                                                                    <div
                                                                        className={`${getCardClasses(status, step)} ${snapshot.isDragging ? 'shadow-xl scale-105 ring-2 ring-green-300' : ''
                                                                            }`}
                                                                        onClick={() => handleCardClick(index)}
                                                                        style={{
                                                                            minWidth: '120px',
                                                                            maxWidth: '180px',
                                                                            opacity: snapshot.isDragging ? 0.9 : 1
                                                                        }}
                                                                    >
                                                                        {/* Single Row Content */}
                                                                        <div className="flex items-center justify-between  w-full px-2 py-1">
                                                                            {/* Left Side: Icon + Step Name */}
                                                                            <div className="flex items-center flex-1 min-w-0">
                                                                                {/* Icon */}
                                                                                <div className="flex-shrink-0 mr-2">
                                                                                    {step.isAddCard ? (
                                                                                        <>
                                                                                            <PlusOutlined
                                                                                                className="text-green-600"
                                                                                                style={{ fontSize: '12px' }}
                                                                                            />
                                                                                            <span style={{ fontSize: "11px" }}
                                                                                                className={`ms-3 font-medium  text-gray-700'
                                                                                            }`}

                                                                                            >
                                                                                                {step.name}
                                                                                            </span>
                                                                                        </>
                                                                                    ) : step.isReviewCard ? (
                                                                                        <>
                                                                                            <EyeOutlined
                                                                                                className="text-gray-600"
                                                                                                style={{ fontSize: '12px' }}
                                                                                            />
                                                                                            <span style={{ fontSize: "11px" }}
                                                                                                className={`ms-3 font-medium  text-gray-700'
                                                                                            }`}

                                                                                            >
                                                                                                {step.name}
                                                                                            </span></>
                                                                                    ) : (


                                                                                        <div className="flex-1 min-w-0">
                                                                                            {editingStepName === index ? (
                                                                                                <div className="flex items-center gap-1">
                                                                                                    <Input
                                                                                                        size="small"
                                                                                                        value={stepNameValue}
                                                                                                        onChange={(e) => setStepNameValue(e.target.value)}
                                                                                                        onPressEnter={() => handleStepNameSave(index)}
                                                                                                        onBlur={() => handleStepNameSave(index)}
                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                        className="text-xs"
                                                                                                        style={{
                                                                                                            fontSize: '11px',
                                                                                                            height: '20px',
                                                                                                            minWidth: '60px',
                                                                                                            maxWidth: '80px'
                                                                                                        }}
                                                                                                        autoFocus
                                                                                                    />
                                                                                                </div>
                                                                                            ) : (
                                                                                                <span style={{ fontSize: "11px" }}
                                                                                                    className={` font-medium truncate block ${status === 'active' || step.isAddCard || step.isReviewCard
                                                                                                        ? step.isAddCard ? 'text-green-600' : step.isReviewCard ? 'text-gray-600' : 'text-white'
                                                                                                        : 'text-gray-700'
                                                                                                        }`}
                                                                                                    title={step.name}
                                                                                                >
                                                                                                    {step.name}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            {/* Right Side: Action Buttons */}
                                                                            {canModify && (
                                                                                <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                                                                                    {/* Edit Button */}
                                                                                    <button
                                                                                        onClick={(e) => handleStepNameEdit(index, e)}
                                                                                        className={`p-0.5 rounded transition-all duration-150 hover:scale-110 ${status === 'active'
                                                                                            ? 'text-white/80 hover:text-white hover:bg-white/20'
                                                                                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                                                                            }`}
                                                                                        title="Edit step name"
                                                                                    >
                                                                                        <EditOutlined style={{ fontSize: '9px' }} />
                                                                                    </button>

                                                                                    {/* Drag Handle */}
                                                                                    {isDraggable && (
                                                                                        <div
                                                                                            {...provided.dragHandleProps}
                                                                                            className={`p-0.5 cursor-move transition-all duration-150 hover:scale-110 ${status === 'active'
                                                                                                ? 'text-white/80 hover:text-white'
                                                                                                : 'text-gray-400 hover:text-gray-600'
                                                                                                }`}
                                                                                            title="Drag to reorder"
                                                                                        >
                                                                                            <HolderOutlined style={{ fontSize: '9px' }} />
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Clone Button */}
                                                                                    <button
                                                                                        onClick={(e) => handleClone(index, e)}
                                                                                        className={`p-0.5 rounded transition-all duration-150 hover:scale-110 ${status === 'active'
                                                                                            ? 'text-white/80 hover:text-white hover:bg-white/20'
                                                                                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                                                                            }`}
                                                                                        title="Clone step"
                                                                                    >
                                                                                        <CopyOutlined style={{ fontSize: '9px' }} />
                                                                                    </button>

                                                                                    {/* Delete Button */}
                                                                                    <button
                                                                                        onClick={(e) => handleDeleteStep(index, e)}
                                                                                        className={`p-0.5 rounded transition-all duration-150 hover:scale-110 ${status === 'active'
                                                                                            ? 'text-white/80 hover:text-white hover:bg-white/20'
                                                                                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                                                                            }`}
                                                                                        title="Delete step"
                                                                                    >
                                                                                        <DeleteOutlined style={{ fontSize: '9px' }} />
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Spacer when sticky */}
            {isSticky && isMobile && <div style={{ height: '48px' }} />}

            <style jsx>{`
                /* Hide scrollbar completely */
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </>
    );
};

export default CardBasedSteps;